import { useState } from 'react';

/**
 * The `DrawerCompanion`'s open/mounted choreography, for a host that opens
 * it on a record: `open` drives the enter/exit animation, `mounted` keeps the
 * panel in the tree through the exit (unmounted by `onExited`), and `record`
 * is what was opened — HELD through the exit so the slide-out stays
 * populated. Shared by the site map's AreaDrawer and the task drawer.
 *
 * `resetKey` is the host's identity (the area shown, the task drawer's open
 * task): when it changes the companion is dropped at once — render-time
 * previous-value pattern — so a companion opened on one host never leaks
 * into the next.
 */
export function useCompanionState<T>(resetKey: unknown) {
  const [record, setRecord] = useState<T | null>(null);
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [renderedKey, setRenderedKey] = useState(resetKey);
  if (resetKey !== renderedKey) {
    setRenderedKey(resetKey);
    setOpen(false);
    setMounted(false);
  }
  const openWith = (next: T) => {
    setRecord(next);
    setMounted(true);
    setOpen(true);
  };
  const close = () => setOpen(false);
  const unmount = () => setMounted(false);
  return { record, open, mounted, openWith, close, unmount };
}
